import type { OfferingModel } from '@sosocio/creator-types/database';
import {
	getHexFromAny,
	validateColor,
} from '@sosocio/frontend-utils/color';

interface OfferingPreviewColorOptions {
	forceOpaque?: boolean;
}

/**
 * Checks whether a print effect is previewed with the `#embossing` SVG filter.
 * Metalic engraving is deliberately left unfiltered.
 *
 * @param printEffect Print treatment configured on the active {@link OfferingModel}.
 * @returns Whether the emboss filter applies.
 */
export function hasSvgEmbossFilter(printEffect: OfferingModel['printEffect']): boolean {
	return (
		printEffect === 'embossing'
		|| printEffect === 'engraving'
		|| printEffect === 'laser engraving'
	);
}

/**
 * Applies the visual filter used to preview embossing and engraving print effects.
 * Unsupported effects deliberately leave the supplied SVG unchanged.
 *
 * @param svgElement SVG root that will be rasterized by the shared renderer.
 * @param printEffect Print treatment configured on the active {@link OfferingModel}.
 */
export function addSvgPrintEffect(
	svgElement: SVGElement,
	printEffect: OfferingModel['printEffect'],
): void {
	if (!hasSvgEmbossFilter(printEffect)) {
		return;
	}

	const documentModel = svgElement.ownerDocument;

	if (!documentModel) {
		return;
	}

	const filterElement = documentModel.createElementNS(
		'http://www.w3.org/2000/svg',
		'filter',
	);
	const convolutionElement = documentModel.createElementNS(
		'http://www.w3.org/2000/svg',
		'feConvolveMatrix',
	);

	filterElement.id = 'embossing';
	convolutionElement.setAttribute(
		'order',
		'3',
	);
	convolutionElement.setAttribute(
		'kernelMatrix',
		'0 1 3 0 0 0 -3 0 0',
	);
	convolutionElement.setAttribute(
		'divisor',
		'1',
	);
	convolutionElement.setAttribute(
		'bias',
		'0',
	);
	filterElement.appendChild(convolutionElement);
	svgElement.prepend(filterElement);
	svgElement.style.filter = 'url(#embossing)';
}

/**
 * Resolves a single-color offering preview while leaving full-color, PMS, and
 * invalid spectrum contracts unchanged. Alpha is preserved unless the caller
 * explicitly requests an opaque canvas color.
 *
 * @param offeringModel Offering preview spectrum supplied by the application.
 * @param options Conversion options for the consuming preview.
 * @returns Normalized preview color, or `undefined` for an absent spectrum, palette, or conversion error.
 */
export function getOfferingPreviewColor(
	offeringModel: Pick<OfferingModel, 'previewColorSpectrum'> | undefined,
	options: OfferingPreviewColorOptions = {},
): string | undefined {
	const spectrum = offeringModel?.previewColorSpectrum;

	if (
		!spectrum
		|| spectrum === 'srgb'
		|| spectrum === 'cmyk'
		|| spectrum.startsWith('pms_')
	) {
		return undefined;
	}

	try {
		const expandedSpectrum = spectrum.replace(
			/^#([\da-f])([\da-f])([\da-f])([\da-f])$/i,
			'#$1$1$2$2$3$3$4$4',
		);
		const convertedColor = getHexFromAny(expandedSpectrum);
		const opaqueColor = convertedColor.slice(
			0,
			7,
		);
		const hasHexAlpha = /^#[\da-f]{8}$/i.test(expandedSpectrum);

		if (
			!/^#[\da-f]{6}(?:[\da-f]{2})?$/i.test(convertedColor)
			|| (
				opaqueColor === '#000000'
				&& spectrum.toLowerCase() !== 'black'
				&& !validateColor(spectrum)
				&& !hasHexAlpha
			)
		) {
			return undefined;
		}

		return options.forceOpaque
			? opaqueColor
			: convertedColor;
	} catch {
		return undefined;
	}
}
